import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FaBox, FaCalendarAlt, FaCheckCircle, FaLayerGroup, FaPercent, FaSearch, FaTags, FaTrash } from 'react-icons/fa';
import useApi from '../../../../utils/api-manager/Helper/useApi';
import AuthContext from '../../../../utils/secure-route/AuthContext';
import { useToastHandler } from '../../../common/ToastHandler';
import LoadingSpinner from '../Orders/LoadingSpinner';
import useSwal from '../../../common/ReusableComponent/useSwal';
import { useStore } from '../../../../context/StoreContext';
import './DealOfTheWeek.css';

const today = new Date().toISOString().slice(0, 10);

const getProductId = (product) => String(product?.id || product?.product_id || '');
const getProductName = (product) => product?.title || product?.product_name || product?.name || product?.barcode || 'Unnamed product';
const getProductCategory = (product) => product?.categories?.[0]?.name || product?.category_name || product?.category || 'General';
const getProductPrice = (product) => Number(product?.segments?.[0]?.selling_price || product?.selling_price || product?.product_price || product?.price || 0);
const getProductStock = (product) => Number(product?.current_stock || product?.total_current_stock || product?.quantity || product?.stock || 0);

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.rawResponse)) return value.rawResponse;
  if (Array.isArray(value?.rawResponse?.results)) return value.rawResponse.results;
  if (Array.isArray(value?.rawResponse?.data)) return value.rawResponse.data;
  if (Array.isArray(value?.data?.results)) return value.data.results;
  if (Array.isArray(value?.data?.data)) return value.data.data;
  return [];
};
const dealProductCache = new Map();
const dealProductRequests = new Map();

const normalizeMediaPath = (value) => {
  if (!value) return '';
  if (value.startsWith('http')) {
    const mediaIndex = value.indexOf('/media/');
    return mediaIndex >= 0 ? value.slice(mediaIndex + '/media'.length) : value;
  }
  return value.startsWith('/media/') ? value.replace('/media', '') : value;
};

const DealOfTheWeek = () => {
  const { user } = useContext(AuthContext);
  const { Get, Post, Delete, GetPaginatedData, getAPI, getHost } = useApi();
  const { handleToast } = useToastHandler();
  const { confirm } = useSwal();
  const { currentStoreId } = useStore();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resolvedStoreId, setResolvedStoreId] = useState(null);
  const [products, setProducts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [selected, setSelected] = useState({});
  const [form, setForm] = useState({
    discount_percentage: 10,
    start_date: today,
    end_date: today,
  });

  const storeId = resolvedStoreId || currentStoreId || user?.store;
  const domain = window.location.hostname;

  const buildImageUrl = (value) => {
    if (!value) return '';
    if (value.startsWith('http')) return value;
    return `${getHost()}${value.startsWith('/media/') ? value : `/media${value.startsWith('/') ? value : `/${value}`}`}`;
  };

  const fetchProducts = async () => {
    if (!storeId) return [];
    const cacheKey = String(storeId);
    if (dealProductCache.has(cacheKey)) return dealProductCache.get(cacheKey);
    if (!dealProductRequests.has(cacheKey)) {
      // The server caps how many products a single page can return
      // (previously as low as 100), so requesting a huge page_size and
      // reading only page 1 silently truncated any store with more
      // products than that cap - this is what "only the first N show up"
      // on the Deals page traced back to. Loop over every page here so the
      // full catalog loads regardless of how many products a store has;
      // this page's own search/category filters (below) already handle
      // narrowing that full list down for the user, so it still needs the
      // whole thing in memory. Capped at 100 pages as a sane backstop
      // against ever looping forever on an unexpected response shape.
      const fetchAllPages = async () => {
        const allProducts = [];
        let page = 1;
        let hasMore = true;
        while (hasMore && page <= 100) {
          const response = await GetPaginatedData('publicProducts', {
            page,
            search: '',
            queryParams: { store_id: storeId, page_size: 1000 }
          });
          const pageProducts = toArray(response);
          allProducts.push(...pageProducts);
          hasMore = Boolean(response?.nextPage) && pageProducts.length > 0;
          page += 1;
        }
        return allProducts;
      };
      dealProductRequests.set(cacheKey, fetchAllPages().finally(() => dealProductRequests.delete(cacheKey)));
    }
    const data = await dealProductRequests.get(cacheKey);
    dealProductCache.set(cacheKey, data);
    return data;
  };

  const fetchDeals = async () => {
    if (!storeId) return [];
    const response = await Get(`${getAPI('dealOfTheWeek')}?store=${storeId}&store_id=${storeId}&domain=${domain}&page_size=100`);
    return toArray(response);
  };

  const loadData = async () => {
    setLoading(true);
    const [productResult, dealResult] = await Promise.allSettled([fetchProducts(), fetchDeals()]);

    if (productResult.status === 'fulfilled') {
      const productRows = productResult.value;
      setProducts(productRows);
    } else {
      setProducts([]);
      handleToast({ type: 'ERROR', message: productResult.reason?.response?.data?.error || 'Failed to load products for deals' });
    }

    if (dealResult.status === 'fulfilled') {
      setDeals(dealResult.value);
    } else {
      setDeals([]);
      handleToast({ type: 'ERROR', message: 'Deals list could not be loaded, but you can still select products' });
    }

    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const resolveStore = async () => {
      const directStoreId = currentStoreId || user?.store;
      if (directStoreId) {
        setResolvedStoreId(directStoreId);
        return;
      }

      try {
        const response = await Get('getAllStoresOfUser');
        const firstStoreId =
          response?.first_store?.id ||
          response?.first_store?.store_id ||
          response?.stores?.[0]?.id ||
          response?.stores?.[0]?.store_id ||
          response?.results?.[0]?.id ||
          response?.results?.[0]?.store_id ||
          response?.[0]?.id ||
          response?.[0]?.store_id;
        if (mounted && firstStoreId) {
          setResolvedStoreId(firstStoreId);
        }
      } catch (error) {
        if (mounted) {
          setLoading(false);
          handleToast({ type: 'ERROR', message: 'Unable to resolve store for deals' });
        }
      }
    };

    resolveStore();

    return () => {
      mounted = false;
    };
    // API helper functions are not stable references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoreId, user?.store]);

  useEffect(() => {
    if (storeId) {
      loadData();
    } else {
      setLoading(false);
    }
    // API helper functions are not stable references, so this effect must only
    // follow the actual store/domain context that changes what data is loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, domain]);

  const categories = useMemo(() => {
    return Array.from(new Set(products.map(getProductCategory))).sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return products.filter((product) => {
      const matchesCategory = category === 'all' || getProductCategory(product) === category;
      const haystack = [
        getProductName(product),
        getProductCategory(product),
        product?.barcode,
        product?.code_sku,
        getProductId(product),
      ].filter(Boolean).join(' ').toLowerCase();
      return matchesCategory && (!query || haystack.includes(query));
    });
  }, [category, products, search]);

  const selectedProducts = useMemo(() => {
    return products.filter((product) => selected[getProductId(product)]);
  }, [products, selected]);

  const toggleProduct = (product) => {
    const productId = getProductId(product);
    if (!productId) return;
    setSelected((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  const selectVisible = () => {
    const next = { ...selected };
    filteredProducts.forEach((product) => {
      const productId = getProductId(product);
      if (productId) next[productId] = true;
    });
    setSelected(next);
  };

  const selectCategory = () => {
    if (category === 'all') {
      handleToast({ type: 'ERROR', message: 'Choose a category first' });
      return;
    }
    const next = { ...selected };
    products.filter((product) => getProductCategory(product) === category).forEach((product) => {
      const productId = getProductId(product);
      if (productId) next[productId] = true;
    });
    setSelected(next);
  };

  const createDeals = async () => {
    if (!storeId) {
      handleToast({ type: 'ERROR', message: 'Store was not found for this user' });
      return;
    }
    if (!selectedProducts.length) {
      handleToast({ type: 'ERROR', message: 'Select at least one product or category' });
      return;
    }
    const discount = Number(form.discount_percentage);
    if (!discount || discount <= 0 || discount >= 100) {
      handleToast({ type: 'ERROR', message: 'Discount must be between 1 and 99%' });
      return;
    }
    if (!form.start_date || !form.end_date || new Date(form.start_date) > new Date(form.end_date)) {
      handleToast({ type: 'ERROR', message: 'Please select a valid date range' });
      return;
    }

    setSaving(true);
    try {
      const url = `${getAPI('dealOfTheWeek')}?store=${storeId}&store_id=${storeId}&domain=${domain}`;
      let created = 0;

      for (const product of selectedProducts) {
        const sellingPrice = getProductPrice(product);
        const discountPrice = Math.max(0, sellingPrice - (sellingPrice * discount / 100));
        await Post(url, {
          store: storeId,
          product_id: getProductId(product),
          product_name: getProductName(product),
          product_image: normalizeMediaPath(product?.product_image || product?.image || product?.primary_image || ''),
          selling_price: Number(sellingPrice.toFixed(2)),
          discount_price: Number(discountPrice.toFixed(2)),
          start_date: form.start_date,
          end_date: form.end_date,
          is_active: true,
        });
        created += 1;
      }

      handleToast({ type: 'SUCCESS', message: `${created} deal${created > 1 ? 's' : ''} created` });
      setSelected({});
      await loadData();
    } catch (error) {
      handleToast({ type: 'ERROR', message: error?.response?.data?.error || error?.response?.data?.detail || 'Failed to create deal' });
    } finally {
      setSaving(false);
    }
  };

  const deleteDeal = async (deal) => {
    const result = await confirm({
      title: 'Delete deal?',
      text: `Remove ${deal.product_name || 'this product'} from deals?`,
      icon: 'warning',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;

    setSaving(true);
    try {
      await Delete('dealOfTheWeek', `${deal.id}?store=${storeId}&store_id=${storeId}&domain=${domain}`);
      handleToast({ type: 'SUCCESS', message: 'Deal deleted' });
      await loadData();
    } catch (error) {
      handleToast({ type: 'ERROR', message: 'Failed to delete deal' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="deal-loading-container">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="deal-of-week-container deal-admin-shell">
      {saving && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}

      <section className="deal-command-card">
        <div className="deal-command-copy">
          <span className="deal-eyebrow"><FaTags /> Deal Center</span>
          <h3>Create product or category deals</h3>
          <p>Search products, choose an entire category, set the discount and publish the offer to your storefront.</p>
        </div>
        <div className="deal-form-panel">
          <label>
            <FaPercent /> Discount %
            <input
              type="number"
              min="1"
              max="99"
              value={form.discount_percentage}
              onChange={(event) => setForm((prev) => ({ ...prev, discount_percentage: event.target.value }))}
            />
          </label>
          <label>
            <FaCalendarAlt /> Start
            <input
              type="date"
              value={form.start_date}
              onChange={(event) => setForm((prev) => ({ ...prev, start_date: event.target.value }))}
            />
          </label>
          <label>
            <FaCalendarAlt /> End
            <input
              type="date"
              value={form.end_date}
              onChange={(event) => setForm((prev) => ({ ...prev, end_date: event.target.value }))}
            />
          </label>
          <button className="deal-primary-btn" onClick={createDeals} disabled={saving || !selectedProducts.length}>
            Publish {selectedProducts.length || ''} Deal{selectedProducts.length > 1 ? 's' : ''}
          </button>
        </div>
      </section>

      <section className="deal-section">
        <div className="deal-tools-row">
          <div className="deal-search-box">
            <FaSearch />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search product, SKU or barcode" />
          </div>
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="all">All categories</option>
            {categories.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
          <button type="button" onClick={selectVisible}>Select visible</button>
          <button type="button" onClick={selectCategory}><FaLayerGroup /> Select category</button>
          <button type="button" onClick={() => setSelected({})}>Clear</button>
        </div>

        <div className="deal-summary-strip">
          <span><FaBox /> {filteredProducts.length} products shown</span>
          <span><FaCheckCircle /> {selectedProducts.length} selected</span>
          <span><FaTags /> {deals.length} active/scheduled deals</span>
        </div>

        <div className="deal-products-table-wrap">
          <table className="deal-products-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Product</th>
                <th>Category</th>
                <th>Stock</th>
                <th>Price</th>
                <th>Deal Price</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => {
                const productId = getProductId(product);
                const price = getProductPrice(product);
                const discountPrice = price - (price * Number(form.discount_percentage || 0) / 100);
                return (
                  <tr key={productId} className={selected[productId] ? 'selected' : ''} onClick={() => toggleProduct(product)}>
                    <td><input type="checkbox" checked={Boolean(selected[productId])} readOnly /></td>
                    <td>
                      <div className="deal-product-cell">
                        <img src={buildImageUrl(product?.product_image || product?.image || product?.primary_image || '')} alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
                        <div>
                          <strong>{getProductName(product)}</strong>
                          <small>{product?.barcode || product?.code_sku || productId}</small>
                        </div>
                      </div>
                    </td>
                    <td>{getProductCategory(product)}</td>
                    <td>{getProductStock(product)}</td>
                    <td>₹{price.toFixed(2)}</td>
                    <td>₹{Math.max(0, discountPrice).toFixed(2)}</td>
                  </tr>
                );
              })}
              {!filteredProducts.length && (
                <tr><td colSpan="6" className="deal-empty">No products found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="deal-section">
        <div className="section-header-deal">
          <div className="header-content">
            <h3><FaTags /> Current Deals</h3>
            <p className="section-description">Deals currently saved for this storefront.</p>
          </div>
        </div>
        <div className="deals-list">
          {deals.map((deal) => (
            <div className="deal-row-card" key={deal.id}>
              <div className="deal-product-cell">
                <img src={buildImageUrl(deal.product_image_url || deal.product_image || '')} alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
                <div>
                  <strong>{deal.product_name || 'Deal product'}</strong>
                  <small>{deal.start_date} to {deal.end_date}</small>
                </div>
              </div>
              <span>{Number(deal.discount_percentage || 0).toFixed(2)}% off</span>
              <span>₹{Number(deal.selling_price || 0).toFixed(2)} → ₹{Number(deal.discount_price || 0).toFixed(2)}</span>
              <button className="deal-danger-btn" onClick={() => deleteDeal(deal)}><FaTrash /> Delete</button>
            </div>
          ))}
          {!deals.length && <div className="deal-empty">No deals created yet.</div>}
        </div>
      </section>
    </div>
  );
};

export default DealOfTheWeek;
